import { calcularInventario } from '../../services/valoracion';
import {
  filterMovimientos,
  filterProductosById,
  calcularStockHasta,
  obtenerPrecioPromedioHasta,
  MOVEMENT_FILTER,
  getDefaultExportDateFrom,
  getDefaultExportDateTo,
} from '../../utils/inventarioFilters';
import { isDateInRange, toLocalDateKey } from '../../../retail-pos/utils/exportRetailData';

export const REPORT_TYPES = {
  COMPRAS: 'COMPRAS',
  VENTAS: 'VENTAS',
  SALIDAS: 'SALIDAS',
  GASTOS: 'GASTOS',
  INVENTARIO: 'INVENTARIO',
  UTILIDAD_BRUTA: 'UTILIDAD_BRUTA',
  UTILIDAD_OPERATIVA: 'UTILIDAD_OPERATIVA',
  FLUJO_CAJA: 'FLUJO_CAJA',
  RESUMEN: 'RESUMEN',
};

export const REPORT_TYPE_LABELS = {
  [REPORT_TYPES.COMPRAS]: 'Compras',
  [REPORT_TYPES.VENTAS]: 'Ventas',
  [REPORT_TYPES.SALIDAS]: 'Salidas',
  [REPORT_TYPES.GASTOS]: 'Gastos',
  [REPORT_TYPES.INVENTARIO]: 'Inventario',
  [REPORT_TYPES.UTILIDAD_BRUTA]: 'Utilidad Bruta',
  [REPORT_TYPES.UTILIDAD_OPERATIVA]: 'Utilidad Operativa',
  [REPORT_TYPES.RESUMEN]: 'Resumen General',
};

export const GROUPING = {
  DAILY: 'DIARIO',
  WEEKLY: 'SEMANAL',
  MONTHLY: 'MENSUAL',
  YEARLY: 'ANUAL',
};

export const GROUPING_LABELS = {
  [GROUPING.DAILY]: 'Diario',
  [GROUPING.WEEKLY]: 'Semanal',
  [GROUPING.MONTHLY]: 'Mensual',
  [GROUPING.YEARLY]: 'Anual',
};

export const METODO_PAGO = {
  ALL: 'TODOS',
  EFECTIVO: 'EFECTIVO',
  QR: 'QR',
};

const MONTH_NAMES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

export function createDefaultReportesFilters() {
  return {
    dateFrom: getDefaultExportDateFrom(),
    dateTo: getDefaultExportDateTo(),
    reportType: REPORT_TYPES.VENTAS,
    agrupacion: GROUPING.MONTHLY,
    productId: 'TODOS',
    metodoPago: METODO_PAGO.ALL,
  };
}

export function reportUsesProductFilter(reportType) {
  return ![
    REPORT_TYPES.GASTOS,
    REPORT_TYPES.FLUJO_CAJA,
    REPORT_TYPES.RESUMEN,
    REPORT_TYPES.UTILIDAD_OPERATIVA,
  ].includes(reportType);
}

export function reportUsesMetodoPago(reportType) {
  return [
    REPORT_TYPES.COMPRAS,
    REPORT_TYPES.VENTAS,
    REPORT_TYPES.GASTOS,
  ].includes(reportType);
}

function parseLocalDate(fecha) {
  const key = toLocalDateKey(fecha);
  if (!key) return null;
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function getISOWeekKey(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const weekNum =
    1 +
    Math.round(
      ((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
    );
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

export function getPeriodKey(fecha, agrupacion = GROUPING.MONTHLY) {
  const key = toLocalDateKey(fecha);
  if (!key) return 'unknown';
  const [y, m] = key.split('-').map(Number);
  const date = parseLocalDate(fecha);

  switch (agrupacion) {
    case GROUPING.DAILY:
      return key;
    case GROUPING.WEEKLY:
      return date ? getISOWeekKey(date) : key;
    case GROUPING.MONTHLY:
      return `${y}-${String(m).padStart(2, '0')}`;
    case GROUPING.YEARLY:
      return String(y);
    default:
      return key;
  }
}

export function getPeriodLabel(periodKey, agrupacion = GROUPING.MONTHLY) {
  if (!periodKey || periodKey === 'unknown') return 'Sin fecha';

  switch (agrupacion) {
    case GROUPING.DAILY: {
      const [y, m, d] = periodKey.split('-').map(Number);
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    case GROUPING.WEEKLY: {
      const [year, weekPart] = periodKey.split('-W');
      return `Sem ${weekPart} · ${year}`;
    }
    case GROUPING.MONTHLY: {
      const [y, m] = periodKey.split('-').map(Number);
      return `${MONTH_NAMES[m - 1]} ${y}`;
    }
    case GROUPING.YEARLY:
      return periodKey;
    default:
      return periodKey;
  }
}

function filterMovimientosConPago(movimientos, filters, movementType) {
  let list = filterMovimientos(movimientos, {
    ...filters,
    movementType,
  }).filter((m) => !m.anulado);

  const { metodoPago = METODO_PAGO.ALL } = filters;
  if (metodoPago && metodoPago !== METODO_PAGO.ALL) {
    list = list.filter((m) => (m.metodoPago || 'EFECTIVO') === metodoPago);
  }
  return list;
}

function createPeriodBucket(key, agrupacion) {
  return {
    key,
    label: getPeriodLabel(key, agrupacion),
    total: 0,
    cantidad: 0,
    costoPromedio: 0,
    precioPromedio: 0,
    valorEstimado: 0,
    ventas: 0,
    costos: 0,
    utilidadBruta: 0,
    utilidadOperativa: 0,
    compras: 0,
    gastos: 0,
    ingresos: 0,
    egresos: 0,
    balance: 0,
    stock: 0,
    valor: 0,
    categorias: {},
  };
}

function finalizePeriods(periodMap, agrupacion) {
  const periodos = [...periodMap.values()]
    .map((p) => {
      if (p.cantidad > 0) {
        if (p.total > 0 && !p.precioPromedio) {
          p.precioPromedio = p.total / p.cantidad;
        }
        if (p.total > 0 && !p.costoPromedio) {
          p.costoPromedio = p.total / p.cantidad;
        }
      }
      if (p.ingresos || p.egresos) {
        p.balance = p.ingresos - p.egresos;
      }
      if (p.ventas || p.costos) {
        p.utilidadBruta = p.ventas - p.costos;
      }
      if (p.ventas || p.costos || p.gastos) {
        p.utilidadOperativa = p.utilidadBruta - p.gastos;
      }
      return p;
    })
    .sort((a, b) => a.key.localeCompare(b.key));

  periodos.forEach((p) => {
    if (!p.label) p.label = getPeriodLabel(p.key, agrupacion);
  });

  return periodos;
}

function getProductMap(productos = []) {
  return new Map((productos || []).map((p) => [p.id, p]));
}

function ensureMaterialMap(bucket) {
  if (!bucket._materiales) bucket._materiales = new Map();
  return bucket._materiales;
}

function accumulateMaterial(materialMap, productoId, cantidad, total) {
  const id = productoId || 'unknown';
  if (!materialMap.has(id)) {
    materialMap.set(id, { productoId: id, cantidad: 0, total: 0 });
  }
  const row = materialMap.get(id);
  row.cantidad += cantidad;
  row.total += total;
}

function finalizeMateriales(materialMap, productMap) {
  return [...materialMap.values()]
    .map((row) => ({
      productoId: row.productoId,
      material: productMap.get(row.productoId)?.nombre || 'Sin nombre',
      cantidad: row.cantidad,
      total: row.total,
      precioPromedio: row.cantidad > 0 ? row.total / row.cantidad : 0,
    }))
    .sort((a, b) => a.material.localeCompare(b.material, 'es', { sensitivity: 'base' }));
}

function aggregateMovimientos(movimientos, filters, agrupacion, metricFn, { productMap = null } = {}) {
  const trackMaterials = Boolean(productMap) && filters.productId === 'TODOS';
  const periodMap = new Map();

  movimientos.forEach((m) => {
    const key = getPeriodKey(m.fecha, agrupacion);
    if (!periodMap.has(key)) {
      periodMap.set(key, createPeriodBucket(key, agrupacion));
    }
    const bucket = periodMap.get(key);
    const materialMap = trackMaterials ? ensureMaterialMap(bucket) : null;
    metricFn(bucket, m, materialMap);
  });

  const periodos = finalizePeriods(periodMap, agrupacion);

  if (trackMaterials) {
    periodos.forEach((p) => {
      const bucket = periodMap.get(p.key);
      p.materiales = bucket?._materiales
        ? finalizeMateriales(bucket._materiales, productMap)
        : [];
    });
  }

  return periodos;
}

export function buildCompras(movimientos, filters, productos = []) {
  const productMap = getProductMap(productos);
  const list = filterMovimientosConPago(movimientos, filters, MOVEMENT_FILTER.COMPRAS);
  const periodos = aggregateMovimientos(
    list,
    filters,
    filters.agrupacion,
    (bucket, m, materialMap) => {
      const qty = parseFloat(m.cantidad) || 0;
      const monto = qty * (parseFloat(m.precio) || 0);
      bucket.total += monto;
      bucket.cantidad += qty;
      if (materialMap) accumulateMaterial(materialMap, m.productoId, qty, monto);
    },
    { productMap }
  );

  periodos.forEach((p) => {
    p.costoPromedio = p.cantidad > 0 ? p.total / p.cantidad : 0;
  });

  return {
    periodos,
    metricKey: 'total',
    metricLabel: 'Compras',
    totales: {
      total: periodos.reduce((a, p) => a + p.total, 0),
      cantidad: periodos.reduce((a, p) => a + p.cantidad, 0),
    },
  };
}

export function buildVentas(movimientos, filters, productos = []) {
  const productMap = getProductMap(productos);
  const list = filterMovimientosConPago(movimientos, filters, MOVEMENT_FILTER.VENTAS);
  const periodos = aggregateMovimientos(
    list,
    filters,
    filters.agrupacion,
    (bucket, m, materialMap) => {
      const qty = parseFloat(m.cantidad) || 0;
      const monto = qty * (parseFloat(m.precio) || 0);
      bucket.total += monto;
      bucket.cantidad += qty;
      if (materialMap) accumulateMaterial(materialMap, m.productoId, qty, monto);
    },
    { productMap }
  );

  periodos.forEach((p) => {
    p.precioPromedio = p.cantidad > 0 ? p.total / p.cantidad : 0;
  });

  return {
    periodos,
    metricKey: 'total',
    metricLabel: 'Ventas',
    totales: {
      total: periodos.reduce((a, p) => a + p.total, 0),
      cantidad: periodos.reduce((a, p) => a + p.cantidad, 0),
    },
  };
}

export function buildSalidas(movimientos, filters, productos = []) {
  const valoracion = calcularInventario(movimientos, filters.metodo || 'PROMEDIO');
  const productMap = getProductMap(productos);
  const list = filterMovimientos(movimientos, {
    ...filters,
    movementType: MOVEMENT_FILTER.SALIDAS,
  }).filter((m) => !m.anulado);

  const periodos = aggregateMovimientos(
    list,
    filters,
    filters.agrupacion,
    (bucket, m, materialMap) => {
      const qty = parseFloat(m.cantidad) || 0;
      const costo = valoracion.costos[String(m.id || m._id || m.localId)] || 0;
      const monto = qty * costo;
      bucket.cantidad += qty;
      bucket.valorEstimado += monto;
      if (materialMap) accumulateMaterial(materialMap, m.productoId, qty, monto);
    },
    { productMap }
  );

  return {
    periodos,
    metricKey: 'valorEstimado',
    metricLabel: 'Valor estimado',
    totales: {
      cantidad: periodos.reduce((a, p) => a + p.cantidad, 0),
      valorEstimado: periodos.reduce((a, p) => a + p.valorEstimado, 0),
    },
  };
}

function filterGastosActivos(gastos, filters) {
  const { dateFrom, dateTo, metodoPago = METODO_PAGO.ALL } = filters;
  return (gastos || []).filter((g) => {
    if (g.estado !== 'ACTIVO') return false;
    if (!isDateInRange(g.fecha, dateFrom, dateTo)) return false;
    if (metodoPago && metodoPago !== METODO_PAGO.ALL) {
      if ((g.metodoPago || 'EFECTIVO') !== metodoPago) return false;
    }
    return true;
  });
}

export function buildGastos(gastos, categorias, filters) {
  const list = filterGastosActivos(gastos, filters);
  const catMap = new Map((categorias || []).map((c) => [c.id, c.nombre]));
  const periodMap = new Map();

  list.forEach((g) => {
    const key = getPeriodKey(g.fecha, filters.agrupacion);
    if (!periodMap.has(key)) {
      periodMap.set(key, createPeriodBucket(key, filters.agrupacion));
    }
    const bucket = periodMap.get(key);
    const monto = parseFloat(g.monto) || 0;
    bucket.total += monto;
    const catName = catMap.get(g.categoriaId) || 'Sin categoría';
    bucket.categorias[catName] = (bucket.categorias[catName] || 0) + monto;
  });

  const periodos = finalizePeriods(periodMap, filters.agrupacion);

  return {
    periodos,
    metricKey: 'total',
    metricLabel: 'Gastos',
    totales: {
      total: periodos.reduce((a, p) => a + p.total, 0),
    },
  };
}

export function buildInventario(movimientos, productos, filters) {
  const { dateTo, productId = 'TODOS' } = filters;
  const list = filterProductosById(productos, productId);

  const periodos = list
    .map((p) => {
      const stock = calcularStockHasta(movimientos, p.id, dateTo);
      const costoPromedio = obtenerPrecioPromedioHasta(movimientos, p.id, dateTo, filters.metodo);
      const valor = stock * costoPromedio;
      return {
        key: p.id,
        label: p.nombre,
        stock,
        costoPromedio,
        valor,
        productoId: p.id,
      };
    })
    .sort((a, b) => b.valor - a.valor);

  return {
    periodos,
    metricKey: 'valor',
    metricLabel: 'Valor inventario',
    isProductSnapshot: true,
    totales: {
      stock: periodos.reduce((a, p) => a + p.stock, 0),
      valor: periodos.reduce((a, p) => a + p.valor, 0),
    },
  };
}

function filterGananciasByRange(gananciasDiarias, filters) {
  const { dateFrom, dateTo } = filters;
  return (gananciasDiarias || []).filter((g) =>
    isDateInRange(g.fechaObj || g.fecha, dateFrom, dateTo)
  );
}

function applyProductFilterToDay(dia, productId) {
  if (productId === 'TODOS') {
    const ventas = dia.ventas || 0;
    const costos = dia.costos || 0;
    const gastos = dia.gastos || 0;
    const utilidadBruta = ventas - costos;
    return {
      ventas,
      costos,
      utilidadBruta,
      utilidadOperativa: utilidadBruta - gastos,
      compras: dia.compras || 0,
      gastos,
      inyecciones: dia.inyecciones || 0,
    };
  }

  const detalles = (dia.detalles || []).filter((d) => d.productoId === productId);
  const ventas = detalles.reduce((a, d) => a + (d.ingreso || 0), 0);
  const costos = detalles.reduce((a, d) => a + (d.costo || 0), 0);
  const utilidadBruta = ventas - costos;
  return {
    ventas,
    costos,
    utilidadBruta,
    utilidadOperativa: utilidadBruta,
    compras: 0,
    gastos: 0,
    inyecciones: 0,
  };
}

function groupGanancias(gananciasDiarias, filters) {
  const { agrupacion, productId = 'TODOS' } = filters;
  const filtered = filterGananciasByRange(gananciasDiarias, filters);
  const periodMap = new Map();

  filtered.forEach((dia) => {
    const metrics = applyProductFilterToDay(dia, productId);
    const key = getPeriodKey(dia.fechaObj || dia.fecha, agrupacion);
    if (!periodMap.has(key)) {
      periodMap.set(key, createPeriodBucket(key, agrupacion));
    }
    const bucket = periodMap.get(key);
    bucket.ventas += metrics.ventas;
    bucket.costos += metrics.costos;
    bucket.utilidadBruta += metrics.utilidadBruta;
    bucket.utilidadOperativa += metrics.utilidadOperativa;
    bucket.compras += metrics.compras;
    bucket.gastos += metrics.gastos;
    bucket.inyecciones += metrics.inyecciones;
  });

  return finalizePeriods(periodMap, agrupacion);
}

export function buildUtilidadBruta(gananciasDiarias, filters) {
  const periodos = groupGanancias(gananciasDiarias, filters);

  return {
    periodos,
    metricKey: 'utilidadBruta',
    metricLabel: 'Utilidad Bruta',
    totales: {
      ventas: periodos.reduce((a, p) => a + p.ventas, 0),
      costos: periodos.reduce((a, p) => a + p.costos, 0),
      utilidadBruta: periodos.reduce((a, p) => a + p.utilidadBruta, 0),
    },
  };
}

export function buildUtilidadOperativa(gananciasDiarias, filters) {
  const periodos = groupGanancias(gananciasDiarias, { ...filters, productId: 'TODOS' });

  return {
    periodos,
    metricKey: 'utilidadOperativa',
    metricLabel: 'Utilidad Operativa',
    totales: {
      ventas: periodos.reduce((a, p) => a + p.ventas, 0),
      costos: periodos.reduce((a, p) => a + p.costos, 0),
      gastos: periodos.reduce((a, p) => a + p.gastos, 0),
      utilidadBruta: periodos.reduce((a, p) => a + p.utilidadBruta, 0),
      utilidadOperativa: periodos.reduce((a, p) => a + p.utilidadOperativa, 0),
    },
  };
}

export function buildFlujoCaja(gananciasDiarias, filters) {
  const periodos = groupGanancias(gananciasDiarias, { ...filters, productId: 'TODOS' }).map((p) => {
    const ingresos = p.ventas + Math.max(p.inyecciones, 0);
    const egresos = p.compras + p.gastos + Math.max(-p.inyecciones, 0);
    return {
      ...p,
      ingresos,
      egresos,
      balance: ingresos - egresos,
    };
  });

  return {
    periodos,
    metricKey: 'balance',
    metricLabel: 'Balance',
    totales: {
      ingresos: periodos.reduce((a, p) => a + p.ingresos, 0),
      egresos: periodos.reduce((a, p) => a + p.egresos, 0),
      balance: periodos.reduce((a, p) => a + p.balance, 0),
    },
  };
}

export function buildResumenGeneral(gananciasDiarias, filters) {
  const periodos = groupGanancias(gananciasDiarias, { ...filters, productId: 'TODOS' }).map((p) => {
    const ingresos = p.ventas + Math.max(p.inyecciones, 0);
    const egresos = p.compras + p.gastos + Math.max(-p.inyecciones, 0);
    return {
      ...p,
      ingresos,
      egresos,
      balance: ingresos - egresos,
      utilidadBruta: p.ventas - p.costos,
      utilidadOperativa: p.utilidadBruta - p.gastos,
    };
  });

  return {
    periodos,
    metricKey: 'ventas',
    metricLabel: 'Ventas',
    totales: {
      compras: periodos.reduce((a, p) => a + p.compras, 0),
      ventas: periodos.reduce((a, p) => a + p.ventas, 0),
      gastos: periodos.reduce((a, p) => a + p.gastos, 0),
      utilidadBruta: periodos.reduce((a, p) => a + p.utilidadBruta, 0),
      utilidadOperativa: periodos.reduce((a, p) => a + p.utilidadOperativa, 0),
      balance: periodos.reduce((a, p) => a + p.balance, 0),
    },
  };
}

export function computeKpis(periodos, metricKey) {
  const values = (periodos || []).map((p) => parseFloat(p[metricKey]) || 0);
  if (values.length === 0) {
    return { total: 0, promedio: 0, maximo: 0, minimo: 0, variacion: null };
  }

  const total = values.reduce((a, b) => a + b, 0);
  const promedio = total / values.length;
  const maximo = Math.max(...values);
  const minimo = Math.min(...values);
  const first = values[0];
  const last = values[values.length - 1];
  let variacion = null;
  if (first !== 0) {
    variacion = ((last - first) / Math.abs(first)) * 100;
  } else if (last !== 0) {
    variacion = 100;
  }

  return { total, promedio, maximo, minimo, variacion };
}

export function buildReporte({
  reportType,
  movimientos,
  productos,
  gastos,
  categorias,
  gananciasDiarias,
  filters,
}) {
  switch (reportType) {
    case REPORT_TYPES.COMPRAS:
      return buildCompras(movimientos, filters, productos);
    case REPORT_TYPES.VENTAS:
      return buildVentas(movimientos, filters, productos);
    case REPORT_TYPES.SALIDAS:
      return buildSalidas(movimientos, filters, productos);
    case REPORT_TYPES.GASTOS:
      return buildGastos(gastos, categorias, filters);
    case REPORT_TYPES.INVENTARIO:
      return buildInventario(movimientos, productos, filters);
    case REPORT_TYPES.UTILIDAD_BRUTA:
      return buildUtilidadBruta(gananciasDiarias, filters);
    case REPORT_TYPES.UTILIDAD_OPERATIVA:
      return buildUtilidadOperativa(gananciasDiarias, filters);
    case REPORT_TYPES.FLUJO_CAJA:
      return buildFlujoCaja(gananciasDiarias, filters);
    case REPORT_TYPES.RESUMEN:
      return buildResumenGeneral(gananciasDiarias, filters);
    default:
      return buildVentas(movimientos, filters);
  }
}

export function formatMoney(value) {
  const n = parseFloat(value) || 0;
  return n.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
